import React, { useState, useRef, useEffect } from 'react';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm Ermiyas' AI Real Estate Advisor. How can I assist with your real estate needs today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  // Optimized knowledge base
  const realEstateKnowledge = {
    market: "Ethiopian real estate market: 6-8% GDP, $1.33T by 2025, 6.2% annual growth",
    housing: "1.5M+ housing deficit, 300K+ annual demand, 70% informal housing in Addis",
    luxury: "Bole, CMC, Ayat areas: ETB 35-80K/m², 15-25% appreciation, 6-9% yield",
    commercial: "Office: $18-35/m², Retail: $30-70/m², Industrial: $4-8/m²",
    investment: "20-40% developer margins, 15-25% capital appreciation, 6-12% rental yields"
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(transcript);
        setIsListening(false);
      };
    }
  }, []);

  const startListening = () => {
    if (recognitionRef.current) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      setIsListening(false);
      recognitionRef.current.stop();
    }
  };

  const speakResponse = (text) => {
    if ('speechSynthesis' in window && ttsEnabled) {
      speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      speechSynthesis.speak(utterance);
    }
  };

  const generateBotResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    let response = "";
    
    if (message.includes('market') || message.includes('overview')) {
      response = realEstateKnowledge.market;
    } else if (message.includes('housing') || message.includes('deficit')) {
      response = realEstateKnowledge.housing;
    } else if (message.includes('luxury') || message.includes('premium')) {
      response = realEstateKnowledge.luxury;
    } else if (message.includes('commercial') || message.includes('office')) {
      response = realEstateKnowledge.commercial;
    } else if (message.includes('investment') || message.includes('roi')) {
      response = realEstateKnowledge.investment;
    } else if (message.includes('contact') || message.includes('phone')) {
      response = "📞 Call: +251 956445555\n💬 WhatsApp: 0909068030\n📧 Email: ermiyasbedasa4@gmail.com\n🏢 Bole, Millennium Hall front";
    } else if (message.includes('service') || message.includes('help')) {
      response = "Services: Real Estate Advising, Transaction Management, International Support, Digital Marketing, Investment Strategy";
    } else if (message.includes('achievement') || message.includes('success')) {
      response = "✅ 17 apartments sold in one week\n✅ 2B+ ETB total transactions\n✅ 6+ years experience\n✅ Trained 4 major real estate companies";
    } else {
      response = "I can help with market insights, investment analysis, property consultation, and connecting you with Ermiyas. What interests you most?";
    }

    return {
      id: messages.length + 2,
      text: response,
      sender: 'bot',
      timestamp: new Date()
    };
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (inputMessage.trim() === '') return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    setTimeout(() => {
      const botResponse = generateBotResponse(inputMessage);
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
      speakResponse(botResponse.text);
    }, 1000);
  };

  const quickQuestions = [
    "Market overview",
    "Housing deficit", 
    "Investment ROI",
    "Contact Ermiyas",
    "Luxury properties",
    "Commercial spaces"
  ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
    setTimeout(() => {
      const botResponse = generateBotResponse(question);
      setMessages(prev => [...prev, {
        id: messages.length + 1,
        text: question,
        sender: 'user',
        timestamp: new Date()
      }, botResponse]);
      speakResponse(botResponse.text);
    }, 100);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
  };

  return (
    <>
      <style>
        {`
          .chatbot-container {
            position: fixed;
            bottom: 20px;
            right: 20px;
            z-index: 10000;
          }

          .chatbot-button {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background: linear-gradient(135deg, #f3b33d, #e6a830);
            border: 2px solid #f6d69b;
            color: white;
            cursor: pointer;
            box-shadow: 0 8px 25px rgba(243, 179, 61, 0.4);
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
          }

          .chatbot-button:hover {
            transform: scale(1.1) rotate(5deg);
            box-shadow: 0 12px 30px rgba(243, 179, 61, 0.6);
          }

          .chatbot-button::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: linear-gradient(45deg, #f3b33d, #ff6b6b, #4ecdc4);
            border-radius: 50%;
            z-index: -1;
            animation: rotate 3s linear infinite;
            filter: blur(8px);
          }

          @keyframes rotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .chatbot-window {
            position: absolute;
            bottom: 70px;
            right: 0;
            width: 350px;
            height: 500px;
            background: rgba(10, 15, 28, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 20px;
            box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border: 1px solid rgba(243, 179, 61, 0.3);
            animation: slideUp 0.3s ease-out;
          }

          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .chatbot-header {
            background: linear-gradient(135deg, rgba(243, 179, 61, 0.9), rgba(230, 168, 48, 0.8));
            padding: 12px 20px;
            color: #062030;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid rgba(243, 179, 61, 0.3);
          }

          .chatbot-messages {
            flex: 1;
            padding: 15px;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .message {
            max-width: 85%;
            padding: 10px 14px;
            border-radius: 15px;
            font-size: 12px;
            line-height: 1.4;
            animation: messageSlide 0.3s ease-out;
            white-space: pre-line;
          }

          .user-message {
            align-self: flex-end;
            background: linear-gradient(135deg, #f3b33d, #e6a830);
            color: #062030;
            border-bottom-right-radius: 5px;
          }

          .bot-message {
            align-self: flex-start;
            background: rgba(255, 255, 255, 0.1);
            color: #fff;
            border-bottom-left-radius: 5px;
            border: 1px solid rgba(255, 255, 255, 0.1);
          }

          .typing-indicator {
            align-self: flex-start;
            background: rgba(255, 255, 255, 0.1);
            padding: 10px 14px;
            border-radius: 15px;
            display: flex;
            align-items: center;
            gap: 6px;
            color: #9aa7b2;
            font-size: 12px;
            border: 1px solid rgba(255, 255, 255, 0.1);
          }

          .typing-dots {
            display: flex;
            gap: 3px;
          }

          .typing-dot {
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: #f3b33d;
            animation: typingBounce 1.4s infinite ease-in-out;
          }

          .typing-dot:nth-child(1) { animation-delay: -0.32s; }
          .typing-dot:nth-child(2) { animation-delay: -0.16s; }

          @keyframes typingBounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }

          .chatbot-input-form {
            padding: 15px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            display: flex;
            gap: 8px;
            align-items: center;
          }

          .chatbot-input {
            flex: 1;
            padding: 10px 14px;
            border: 1px solid rgba(243, 179, 61, 0.3);
            border-radius: 20px;
            background: rgba(255, 255, 255, 0.05);
            color: white;
            font-size: 12px;
            outline: none;
            transition: all 0.3s ease;
          }

          .chatbot-input:focus {
            border-color: #f3b33d;
            box-shadow: 0 0 0 2px rgba(243, 179, 61, 0.2);
          }

          .voice-button {
            padding: 10px;
            background: ${isListening ? '#ff6b6b' : 'rgba(255, 255, 255, 0.1)'};
            border: none;
            border-radius: 50%;
            color: white;
            cursor: pointer;
            transition: all 0.3s ease;
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .voice-button:hover {
            background: ${isListening ? '#ff5252' : 'rgba(255, 255, 255, 0.2)'};
            transform: scale(1.1);
          }

          .chatbot-send {
            padding: 10px 16px;
            background: linear-gradient(135deg, #f3b33d, #e6a830);
            border: none;
            border-radius: 20px;
            color: #062030;
            cursor: pointer;
            font-size: 12px;
            font-weight: 600;
            transition: all 0.3s ease;
          }

          .chatbot-send:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(243, 179, 61, 0.4);
          }

          .quick-questions {
            display: flex;
            flex-wrap: wrap;
            gap: 5px;
            padding: 10px 15px;
            background: rgba(0, 0, 0, 0.2);
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }

          .quick-question {
            padding: 6px 10px;
            background: rgba(243, 179, 61, 0.1);
            border: 1px solid rgba(243, 179, 61, 0.2);
            border-radius: 12px;
            color: #f3b33d;
            font-size: 10px;
            cursor: pointer;
            transition: all 0.3s ease;
            flex: 1;
            min-width: calc(50% - 5px);
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .quick-question:hover {
            background: rgba(243, 179, 61, 0.2);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(243, 179, 61, 0.3);
          }

          @media (max-width: 480px) {
            .chatbot-container {
              right: 10px;
              bottom: 10px;
            }
            
            .chatbot-window {
              width: 300px;
              right: -5px;
              height: 450px;
            }
            
            .chatbot-button {
              width: 50px;
              height: 50px;
            }
          }
        `}
      </style>

      <div className="chatbot-container">
        {isOpen && (
          <div className="chatbot-window">
            <div className="chatbot-header">
              <span>🤖 Real Estate AI Assistant</span>
              <button 
                onClick={() => setTtsEnabled(!ttsEnabled)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#062030',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {ttsEnabled ? '🔊' : '🔈'}
              </button>
            </div>
            
            <div className="chatbot-messages">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
                >
                  {message.text}
                </div>
              ))}
              
              {isTyping && (
                <div className="typing-indicator">
                  <span>Analyzing...</span>
                  <div className="typing-dots">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="quick-questions">
              {quickQuestions.map((question, index) => (
                <button
                  key={index}
                  className="quick-question"
                  onClick={() => handleQuickQuestion(question)}
                >
                  {question}
                </button>
              ))}
            </div>

            <form onSubmit={handleSendMessage} className="chatbot-input-form">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Ask about real estate..."
                className="chatbot-input"
              />
              <button 
                type="button" 
                className="voice-button"
                onClick={isListening ? stopListening : startListening}
                title={isListening ? 'Stop listening' : 'Start voice input'}
              >
                {isListening ? '⏹️' : '🎤'}
              </button>
              <button type="submit" className="chatbot-send">
                Send
              </button>
            </form>
          </div>
        )}
        
        <button onClick={toggleChat} className="chatbot-button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
            <path d="M7 9h10v2H7zm0 4h7v2H7z"/>
          </svg>
        </button>
      </div>
    </>
  );
};

export default Chatbot;